import { Fragment, useState, useEffect, useMemo, useRef } from "react";
import { useProjectStore } from "../../store/useProjectStore";
import { useClientStore } from "../../store/useClientStore";
import { useBidStore } from "../../store/useBidStore";
import { useProjectTypeStore } from "../../store/useProjectTypeStore";
import { mergeCategoryNames, subcategoriesFor } from "../../config/categoryConfig";
import { useProposalStore } from "../../store/useProposalStore";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import { axiosInstance } from "../../lib/axios";
import toast from "react-hot-toast";
import { AlertTriangle, Sparkles, Upload, X, FileText, CheckCircle2, Plus, KeyRound } from "lucide-react";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import Button from "../ui/Button";
import { useAuthStore } from "../../store/useAuthStore";
import { useFinanceStore } from "../../store/useFinanceStore";
import MilestonesEditor from "./MilestonesEditor";
import ResourceCostsEditor, { resourceCostTotal, type ResourceCost } from "./ResourceCostsEditor";
import CredentialsModal, { type PortalCredentials } from "./CredentialsModal";
import type { Milestone } from "../../store/useProjectStore";
import type { ChecklistItem } from "../../store/useProjectTypeStore";

interface ProjectFormProps {
  onSuccess?: () => void;
  initialBidId?: string;
  // Draft resume: opens the wizard pre-filled from a saved Draft and keeps
  // updating THAT document — finalizing converts it in place (no duplicate).
  initialProject?: any;
}

const WON_BID_STATUSES = ["Successfully Converted", "Awarded Directly"];
const idOf = (v: any): string => ((v && typeof v === "object" ? v._id : v) || "");

const ProjectForm = ({ onSuccess, initialBidId, initialProject }: ProjectFormProps) => {
  const { createProject, isCreatingProject, projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients, createClient } = useClientStore();
  const { bids, fetchMyBids } = useBidStore();
  const { types, fetchTypes } = useProjectTypeStore();
  const { proposals, fetchProposals } = useProposalStore();
  const { questionnaires, getAllQuestionnaires } = useQuestionnaireStore();
  const { authUser } = useAuthStore();
  const { accounts, fetchSummary } = useFinanceStore();

  // Draft workflow state — a saved draft keeps its id so every subsequent save
  // updates the same document, and existing attachments stay visible on resume.
  const init = initialProject && initialProject.isDraft ? initialProject : null;
  const [draftId, setDraftId] = useState<string | null>(init?._id || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<any[]>(init?.attachments || []);

  const [formData, setFormData] = useState({
    name: init?.name || "",
    assignedContact: idOf(init?.assignedContact),
    projectType: init?.projectType || "",
    projectSubType: init?.projectSubType || "",
    proposal: idOf(init?.proposal),
    linkedQuestionnaire: idOf(init?.linkedQuestionnaire),
    description: init?.description || "",
    designType: "",
    website: false,
    graphicDesign: false,
    videoAnimation: false,
    marketing: false,
    trialBased: false,
    supervisor: "",
    status: init?.projectStatus || "Not Started",
    eta: init?.eta ? String(init.eta).split("T")[0] : "",
    projectPrice: init?.projectPrice != null ? String(init.projectPrice) : "",
    billingType: init?.billingType || "Hourly",
    hourlyRate: init?.hourlyRate ? String(init.hourlyRate) : "",
    estimatedHours: init?.estimatedHours ? String(init.estimatedHours) : "",
    escrowAmount: init?.escrowAmount ? String(init.escrowAmount) : "",
    releasedPayment: init?.releasedPayment ? String(init.releasedPayment) : "",
    paymentStatus: init?.paymentStatus || "Pending",
    upworkAccount: idOf(init?.upworkAccount),
    source: init?.source || "Upwork",
    upfrontPayment: init?.upfrontPayment ? String(init.upfrontPayment) : "",
    paymentMethod: init?.paymentMethod || "",
    paymentMethodNote: init?.paymentMethodNote || "",
    sourceBid: idOf(init?.sourceBid),
    milestones: (init?.milestones || []) as Milestone[],
    resourceCosts: (init?.resourceCosts || []) as ResourceCost[],
    // Client Portal Access controls.
    portalAccess: true,
    sendWelcomeEmail: true,
    grantWalletCredits: false,
    welcomeBonusAmount: "",
  });

  const [useMilestones, setUseMilestones] = useState(false);
  const [milestoneCount, setMilestoneCount] = useState(2);

  // Step 1 — how was the project won? Direct client, or a Won bid (an Invitation
  // bid automatically marks the project INVITED — no separate option needed).
  const [originMode, setOriginMode] = useState<"" | "direct" | "bid">(
    init ? (init.source === "Direct" ? "direct" : init.sourceBid ? "bid" : "") : ""
  );
  const [bidSearch, setBidSearch] = useState("");

  // Multi-step wizard: 1 Project Information → 2 Milestones & Payments →
  // 3 Documents & Assets → 4 Review & Create. Steps HIDE (never unmount), so
  // entered data survives moving back and forth; each step validates before
  // the wizard advances — Save as Draft works from ANY step.
  const [step, setStep] = useState(1);
  const STEPS = ["Project Information", "Milestones & Payments", "Documents & Assets", "Review & Create"];

  // Credentials to show after provisioning (when no email / for manual sharing).
  const [portalCreds, setPortalCreds] = useState<{ credentials: PortalCredentials; clientName?: string; emailSent?: boolean } | null>(null);

  // S1 — file attachments (uploaded after the project is created).
  const [files, setFiles] = useState<File[]>([]);
  const [fileCategory, setFileCategory] = useState("Client Asset");
  // Dedicated Job Description upload (PDF / DOCX) — attached as its own category.
  const [jdFile, setJdFile] = useState<File | null>(null);

  // Inline "create contact" modal — a project must have a contact.
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", tenant: "" });
  const [tenantOptions, setTenantOptions] = useState<{ _id: string; name: string }[]>([]);
  useEffect(() => {
    axiosInstance.get("/tenants").then((r) => setTenantOptions(r.data?.tenants || [])).catch(() => {});
  }, []);
  const [creatingContact, setCreatingContact] = useState(false);

  const saveNewContact = async () => {
    if (!newContact.name.trim()) {
      toast.error("Contact name is required");
      return;
    }
    if (!newContact.tenant) {
      toast.error("Brand / Company is required");
      return;
    }
    setCreatingContact(true);
    const created: any = await createClient({
      name: newContact.name.trim(),
      email: newContact.email.trim(),
      phone: newContact.phone.trim(),
      tenant: newContact.tenant,
    } as any);
    setCreatingContact(false);
    if (created?._id) {
      setFormData((p) => ({ ...p, assignedContact: created._id }));
      setContactModalOpen(false);
      setNewContact({ name: "", email: "", phone: "", tenant: "" });
    }
  };

  // S3 — requirement checklist met-state + AI analysis.
  const [checkMet, setCheckMet] = useState<Record<string, boolean>>({});
  const [aiResult, setAiResult] = useState<{ missing: string[]; recommendation: string; aiUsed: boolean } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Baseline checklist every project gets — surfaces blockers (credentials,
  // scope, winning context) BEFORE creation instead of at delivery time.
  const BASE_CHECKLIST: ChecklistItem[] = [
    { label: "Credentials / access received & verified", required: true },
    { label: "Scope & deliverables confirmed with the client", required: true },
    { label: "Winning context / reference documents attached", required: false },
  ];

  // Active types → dropdown; selected type → sub-types + checklist.
  const selectedType = useMemo(() => types.find((t) => t.name === formData.projectType), [types, formData.projectType]);
  const activeSubTypes = useMemo(() => (selectedType?.subTypes || []).filter((s) => s.active), [selectedType]);
  const checklist: ChecklistItem[] = useMemo(() => {
    const base = selectedType?.checklist || [];
    const st = activeSubTypes.find((s) => s.name === formData.projectSubType);
    const configured = st ? [...base, ...(st.checklist || [])] : [...base];
    // Merge baseline items in (configured items win on a label clash).
    const seen = new Set(configured.map((c) => c.label.toLowerCase()));
    return [...configured, ...BASE_CHECKLIST.filter((c) => !seen.has(c.label.toLowerCase()))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, activeSubTypes, formData.projectSubType]);

  // Selected contact — warn if it has no email/phone (won't get automated emails).
  const selectedContact: any = useMemo(
    () => clients.find((c: any) => c._id === formData.assignedContact),
    [clients, formData.assignedContact]
  );
  const contactMissingInfo = selectedContact && (!selectedContact.email || !selectedContact.phone);

  const wonBids = useMemo(
    () => bids.filter((b: any) => WON_BID_STATUSES.includes(b.bidStatus || "")),
    [bids]
  );

  // Search filter over the Won-bid list (title / contact / account).
  const filteredWonBids = useMemo(() => {
    const q = bidSearch.trim().toLowerCase();
    if (!q) return wonBids;
    return wonBids.filter((b: any) =>
      [b.jobTitle, b.contactRef?.name, b.contactRef?.username, b.platformAccount]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [wonBids, bidSearch]);

  const selectedBid: any = useMemo(
    () => bids.find((b: any) => b._id === formData.sourceBid),
    [bids, formData.sourceBid]
  );

  // A proposal already attached to a project stays there — filter used ones out.
  const usedProposalIds = useMemo(() => {
    const s = new Set<string>();
    (projects || []).forEach((p: any) => {
      const id = p.proposal?._id || p.proposal;
      if (id) s.add(String(id));
    });
    return s;
  }, [projects]);
  const availableProposals = useMemo(
    () => proposals.filter((p: any) => !usedProposalIds.has(String(p._id)) || p._id === formData.proposal),
    [proposals, usedProposalIds, formData.proposal]
  );

  // Resolve the freelance account a bid was placed from: the normalized
  // reference when present, otherwise the account name stored on the bid.
  const accountIdForBid = (bid: any): string => {
    if (!bid) return "";
    const ref = bid.freelanceAccount?._id || bid.freelanceAccount;
    if (ref && accounts.some((a: any) => String(a.accountId) === String(ref))) return String(ref);
    const byName = accounts.find(
      (a: any) => String(a.accountName || "").trim().toLowerCase() === String(bid.platformAccount || "").trim().toLowerCase()
    );
    return byName ? String(byName.accountId) : "";
  };

  const applyBid = (bidId: string) => {
    const bid: any = bids.find((b: any) => b._id === bidId);
    if (!bid) {
      setFormData((p) => ({ ...p, sourceBid: "" }));
      return;
    }
    const cleanName = (bid.jobTitle || "")
      .replace(/[^A-Za-z0-9\s\-_.]/g, " ")
      .trim()
      .slice(0, 100);
    setFormData((p) => ({
      ...p,
      sourceBid: bidId,
      name: cleanName || p.name,
      projectPrice: bid.bidAmount ? String(bid.bidAmount) : p.projectPrice,
      // A won invitation bid becomes an Invited project; otherwise it's an Upwork bid-generated project.
      // Invitation AND Directly Awarded wins spent no connects → both map to the
      // "Invitation" project source (excluded from connects-recovery accounting).
      source: ["Invitation", "Directly Awarded"].includes(bid.entryType) ? "Invitation" : "Upwork",
      assignedContact: bid.contactRef?._id || bid.contactRef || p.assignedContact,
      // The bid already names the freelance account it was placed from — carry
      // it over instead of asking the BD for it a second time.
      upworkAccount: accountIdForBid(bid) || p.upworkAccount,
    }));
  };

  // The accounts list can arrive after the bid was applied (or after a draft is
  // resumed) — fill the freelance account in as soon as it is resolvable.
  useEffect(() => {
    if (formData.upworkAccount || !formData.sourceBid || !accounts.length) return;
    const bid: any = bids.find((b: any) => b._id === formData.sourceBid);
    const id = accountIdForBid(bid);
    if (id) setFormData((p) => ({ ...p, upworkAccount: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, bids, formData.sourceBid, formData.upworkAccount]);

  const projectValue = Number(formData.projectPrice) || 0;
  const isDirect = formData.source === "Direct";
  const showMilestonePrompt = projectValue > 100 && formData.milestones.length === 0;

  const autoSplitMilestones = () => {
    const count = Math.max(1, Math.min(12, Number(milestoneCount) || 1));
    if (projectValue <= 0) {
      toast.error("Enter the project value first");
      return;
    }
    const base = Math.floor((projectValue / count) * 100) / 100;
    const rows: Milestone[] = Array.from({ length: count }, (_, i) => ({
      title: `Milestone ${i + 1}`,
      amount:
        i === count - 1
          ? Math.round((projectValue - base * (count - 1)) * 100) / 100
          : base,
      status: i === 0 ? "In Escrow" : "Pending",
      dueDate: null,
      notes: "",
    }));
    setFormData((prev) => ({ ...prev, milestones: rows }));
  };

  useEffect(() => {
  if (!accounts.length) fetchSummary();
}, []);

  useEffect(() => {
    if (authUser?.fullName) {
      setFormData((prev) => ({
        ...prev,
        supervisor: authUser.fullName,
      }));
    }
  }, [authUser]);

  useEffect(() => {
    fetchClients();
    fetchMyBids();
    fetchTypes();
    fetchProposals();
    getAllQuestionnaires();
  }, [fetchClients, fetchMyBids, fetchTypes, fetchProposals, getAllQuestionnaires]);

  // Questionnaires belonging to the selected contact. Only SUBMITTED ones that
  // aren't already linked to another project can be attached here — a linked
  // questionnaire stays with its project and can never be reused.
  const contactQuestionnaires = useMemo(
    () => questionnaires.filter((q: any) => (q.clientId?._id || q.clientId) === formData.assignedContact),
    [questionnaires, formData.assignedContact]
  );
  const eligibleQuestionnaires = useMemo(
    () => contactQuestionnaires.filter((q: any) => q.isSubmitted && !(q.projectId?._id || q.projectId)),
    [contactQuestionnaires]
  );

  // Auto-link the client's filled questionnaire the moment the contact is chosen.
  useEffect(() => {
    if (!formData.assignedContact) {
      if (formData.linkedQuestionnaire) setFormData((p) => ({ ...p, linkedQuestionnaire: "" }));
      return;
    }
    const stillValid = eligibleQuestionnaires.some((q: any) => q._id === formData.linkedQuestionnaire);
    if (!stillValid) {
      setFormData((p) => ({ ...p, linkedQuestionnaire: eligibleQuestionnaires[0]?._id || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.assignedContact, eligibleQuestionnaires]);

  // AI requirement analysis against the configured checklist.
  const runAiCheck = async () => {
    if (!formData.projectType) {
      toast.error("Select a project type first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await axiosInstance.post("/project/analyze-requirements", {
        projectType: formData.projectType,
        projectSubType: formData.projectSubType,
        description: formData.description,
      });
      setAiResult({ missing: data.missing || [], recommendation: data.recommendation || "", aiUsed: data.aiUsed });
      // Reflect AI "present" results into the met-checkboxes.
      const met: Record<string, boolean> = { ...checkMet };
      (data.checklist || []).forEach((c: any) => { met[c.label] = Boolean(c.present); });
      setCheckMet(met);
      if ((data.missing || []).length === 0) toast.success("All requirements look covered");
      else toast(`${data.missing.length} requirement(s) still missing`, { icon: "⚠️" });
    } catch {
      toast.error("AI analysis failed");
    } finally {
      setAiLoading(false);
    }
  };

  // AI-drafts the project description from job details + questionnaire. The BD
  // reviews and edits before saving — the field stays fully editable.
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const generateDescription = async () => {
    if (!formData.name.trim() && !formData.description.trim() && !formData.linkedQuestionnaire) {
      toast.error("Add a project name, paste the job brief, or link a questionnaire first");
      return;
    }
    setAiGenLoading(true);
    try {
      const { data } = await axiosInstance.post("/project/generate-description", {
        name: formData.name,
        projectType: formData.projectType,
        projectSubType: formData.projectSubType,
        jobDetails: formData.description,
        questionnaireId: formData.linkedQuestionnaire || undefined,
      });
      if (data.description) {
        setFormData((prev) => ({ ...prev, description: data.description }));
        toast.success("Description generated — review & edit before saving");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Couldn't generate description");
    } finally {
      setAiGenLoading(false);
    }
  };

  // AI category suggestion — picks from the allowed catalog using the
  // questionnaire answers / job brief; the BD always reviews before saving.
  const [aiCatLoading, setAiCatLoading] = useState(false);
  const [aiCatReason, setAiCatReason] = useState("");
  // AI-generated onboarding requirements — specific to this project rather
  // than a generic per-category list.
  const [aiChecklistLoading, setAiChecklistLoading] = useState(false);
  const [aiChecklist, setAiChecklist] = useState<{ label: string; required: boolean }[]>([]);
  const generateChecklist = async (silent = false) => {
    if (!formData.description.trim()) {
      if (!silent) toast.error("Add a project description first — the checklist is generated from it.");
      return;
    }
    setAiChecklistLoading(true);
    try {
      const { data } = await axiosInstance.post("/project/generate-checklist", {
        projectName: formData.name,
        description: formData.description,
        projectType: formData.projectType,
        projectSubType: formData.projectSubType,
      });
      const items = data.checklist || [];
      setAiChecklist(items);
      if (!silent) toast.success(`${items.length} requirements generated for this project.`);
    } catch (e: any) {
      if (!silent) toast.error(e?.response?.data?.message || "Couldn't generate the checklist");
    } finally {
      setAiChecklistLoading(false);
    }
  };

  // The checklist writes itself once there is a description to work from — the
  // BD never has to remember to ask for it. It regenerates when the category
  // changes, and the button below forces a refresh on demand.
  const checklistSeed = `${formData.projectType}|${formData.description.trim().slice(0, 400)}`;
  const lastChecklistSeed = useRef("");
  useEffect(() => {
    const description = formData.description.trim();
    if (!description || description.length < 40 || !formData.projectType) return;
    if (lastChecklistSeed.current === checklistSeed) return;
    const t = setTimeout(() => {
      lastChecklistSeed.current = checklistSeed;
      generateChecklist(true);
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistSeed]);
  const suggestCategory = async () => {
    if (!formData.name.trim() && !formData.description.trim() && !formData.linkedQuestionnaire) {
      toast.error("Add a project name, job brief, or link a questionnaire first");
      return;
    }
    setAiCatLoading(true);
    try {
      const categories = mergeCategoryNames(types.map((t) => t.name)).map((n) => ({
        name: n,
        subcategories: subcategoriesFor(
          n,
          (types.find((t) => t.name === n)?.subTypes || []).filter((s) => s.active).map((s) => s.name)
        ),
      }));
      const { data } = await axiosInstance.post("/project/suggest-category", {
        name: formData.name,
        jobDetails: formData.description,
        questionnaireId: formData.linkedQuestionnaire || undefined,
        categories,
      });
      if (data.category) {
        setFormData((p) => ({ ...p, projectType: data.category, projectSubType: data.subcategory || "" }));
        setAiCatReason(data.reason || "");
        setAiResult(null);
        toast.success("AI suggested a category — review it against the questionnaire answers / job description");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Couldn't suggest a category");
    } finally {
      setAiCatLoading(false);
    }
  };

  // Prefill from a Won bid when launched from a bid row.
  useEffect(() => {
    if (initialBidId && bids.length) {
      setOriginMode("bid");
      applyBid(initialBidId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBidId, bids.length]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked }: any = e.target;

    if (name === "name") {
      const isValid = /^[A-Za-z\s]*$/.test(value);

      if (!isValid) {
        toast.error("Project name can only contain letters and spaces.");
        return;
      }
    }

    if (name === "projectType") {
      setFormData((prev) => ({ ...prev, projectType: value, projectSubType: "" }));
      setAiResult(null);
      setAiCatReason("");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Per-step validation gates. A milestone row must be fully specified — the
  // backend rejects incomplete milestones too (its invoice is auto-created).
  const milestoneIncomplete = (m: any) =>
    !m.title?.trim() || !m.description?.trim() ||
    !(m.deliverables || []).some((d: any) => (typeof d === "string" ? d : d?.title || "").trim()) ||
    !String(m.duration || "").trim() || !(Number(m.amount) > 0);

  // Effective project value (explicit, else hourly rate × hours).
  const effectiveValue = () =>
    Number(formData.projectPrice) ||
    (formData.billingType === "Hourly"
      ? (Number(formData.hourlyRate) || 0) * (Number(formData.estimatedHours) || 0)
      : 0);

  // `forFinal` gates run only when the project is actually being CREATED —
  // navigating between steps (and Save as Draft) stays possible while the
  // proposal / questionnaire / description are still pending from the client.
  const validateStep = (s: number, forFinal = false): boolean => {
    if (s === 1) {
      if (!originMode) {
        toast.error("Select how the project was won — Direct client, or From a Won bid.");
        return false;
      }
      if (originMode === "bid" && !formData.sourceBid) {
        toast.error("Select the Won bid this project was created from.");
        return false;
      }
      if (!formData.name.trim()) {
        toast.error("Project name is required.");
        return false;
      }
      if (!formData.assignedContact) {
        toast.error("A contact is required. Select one or create a new contact.");
        return false;
      }
      if (!formData.projectType) {
        toast.error("Select the project category.");
        return false;
      }
      if (forFinal && !formData.description.trim()) {
        toast.error("A project description is required.");
        return false;
      }
      if (forFinal && !formData.proposal) {
        toast.error("An originating proposal is required — select the proposal this project was won with. Missing it for now? Save the project as a Draft instead.");
        return false;
      }
      if (forFinal && !formData.linkedQuestionnaire) {
        toast.error("You cannot create the project until a questionnaire is linked. Save it as a Draft, share the questionnaire with the client, and finalize once it's submitted.");
        return false;
      }
    }
    if (s === 2) {
      const bad = formData.milestones.findIndex((m: any) => milestoneIncomplete(m));
      if (bad !== -1) {
        toast.error(`Milestone ${bad + 1} is incomplete — title, description, deliverables, duration and value are all required.`);
        return false;
      }
      // Payment schedule must add up to the project value when milestones are used.
      const value = effectiveValue();
      const msTotal = formData.milestones.reduce((sum: number, m: any) => sum + (Number(m.amount) || 0), 0);
      if (formData.milestones.length > 0 && value > 0 && Math.abs(msTotal - value) > 0.01) {
        toast.error("Payment milestone total does not match the project value. Please review the payment schedule.");
        return false;
      }
    }
    return true; // Step 3 (documents & assets) has no required fields.
  };
  const goNext = () => { if (validateStep(step)) setStep((s) => Math.min(4, s + 1)); };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  // The full form payload — shared by Create, Save as Draft and Finalize so
  // every path persists exactly the same information.
  // The project-specific list REPLACES the generic per-type list: a short set
  // of real blockers beats a long list nobody reads. The configured list is the
  // fallback until the AI one exists.
  const effectiveChecklist = aiChecklist.length ? aiChecklist : checklist;

  const buildPayload = () => ({
    ...formData,
    requirementChecklist: effectiveChecklist.map((c) => ({
      label: c.label,
      required: c.required !== false,
      // What to verify and what kind of requirement it is — both travel to ERP.
      detail: (c as any).detail || "",
      category: (c as any).category || "",
      met: Boolean(checkMet[c.label]),
    })),
    billingType: formData.billingType,
    projectStatus: formData.status,
    hourlyRate: Number(formData.hourlyRate) || 0,
    estimatedHours: Number(formData.estimatedHours) || 0,
    projectPrice: Number(formData.projectPrice) || 0,
    escrowAmount: Number(formData.escrowAmount) || 0,
    releasedPayment: Number(formData.releasedPayment) || 0,
    upfrontPayment: Number(formData.upfrontPayment) || 0,
    welcomeBonusAmount: Number(formData.welcomeBonusAmount) || 0,
    linkedQuestionnaire: formData.linkedQuestionnaire || null,
  });

  // Upload locally-attached files (and the JD) onto a saved project/draft.
  const uploadPendingFiles = async (projectId: string) => {
    if (files.length) {
      try {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("category", fileCategory);
        const { data } = await axiosInstance.post(`/project/${projectId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.attachments) setExistingAttachments(data.attachments);
        setFiles([]);
      } catch {
        toast.error("File upload failed — the files are still attached locally, save again to retry.");
      }
    }
    if (jdFile) {
      try {
        const fd = new FormData();
        fd.append("files", jdFile);
        fd.append("category", "Job Description");
        const { data } = await axiosInstance.post(`/project/${projectId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.attachments) setExistingAttachments(data.attachments);
        setJdFile(null);
      } catch {
        toast.error("JD upload failed — save again to retry.");
      }
    }
  };

  // Save as Draft — from ANY step, with only a name required. The draft keeps
  // everything entered so far (milestones, costs, files) and can be reopened,
  // edited and finalized later from the Projects list.
  const saveDraft = async () => {
    if (!formData.name.trim()) {
      toast.error("Give the project a name before saving the draft.");
      return;
    }
    setSavingDraft(true);
    try {
      const payload = { ...buildPayload(), isDraft: true };
      let id = draftId;
      if (id) {
        await axiosInstance.put(`/project/${id}`, payload);
      } else {
        const { data } = await axiosInstance.post("/project", payload);
        id = data.project?._id || null;
        setDraftId(id);
      }
      if (id) await uploadPendingFiles(id);
      fetchProjects(true);
      toast.success("Draft saved — reopen it anytime from the Projects list to continue.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't save the draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Enter / implicit submits on earlier steps only advance the wizard —
    // the project is created exclusively from the Review step.
    if (step < 4) {
      goNext();
      return;
    }
    if (!validateStep(1, true)) { setStep(1); return; }
    if (!validateStep(2, true)) { setStep(2); return; }

    let created: any = null;
    if (draftId) {
      // Finalize the draft: push the latest form data onto the SAME document,
      // then convert it in place — never a second project (and only one ERP
      // project can ever result from it).
      setFinalizing(true);
      try {
        await axiosInstance.put(`/project/${draftId}`, { ...buildPayload(), isDraft: true });
        const { data } = await axiosInstance.post(`/project/${draftId}/finalize`, {
          portalAccess: formData.portalAccess,
          sendWelcomeEmail: formData.sendWelcomeEmail,
          grantWalletCredits: formData.grantWalletCredits,
          welcomeBonusAmount: Number(formData.welcomeBonusAmount) || 0,
        });
        created = { ...data.project, portalAccount: data.portalAccount || null };
        toast.success("Project created successfully");
        fetchProjects(true);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Couldn't finalize the draft");
        setFinalizing(false);
        return;
      }
      setFinalizing(false);
    } else {
      created = await createProject(buildPayload());
      if (!created?._id) return; // store already showed the error toast
    }

    // Show credentials for manual sharing when a fresh portal account was made.
    const portal = created?.portalAccount;
    let showedCreds = false;
    if (portal?.status === "created" && portal.credentials) {
      const contactName = clients.find((c: any) => c._id === formData.assignedContact)?.name;
      setPortalCreds({ credentials: portal.credentials, clientName: contactName, emailSent: !!portal.emailSent });
      showedCreds = true;
    } else if (portal?.status === "linked") {
      toast.success("Project linked to the client's existing portal.");
    }

    // Upload attached files + JD once we have the project id.
    if (created?._id) await uploadPendingFiles(created._id);

    setFiles([]);
    setJdFile(null);
    setCheckMet({});
    setAiResult(null);
    setFormData({
      name: "",
      assignedContact: "",
      projectType: "",
      projectSubType: "",
      proposal: "",
      linkedQuestionnaire: "",
      description: "",
      designType: "",
      website: false,
      graphicDesign: false,
      videoAnimation: false,
      marketing: false,
      trialBased: false,
      supervisor: authUser?.fullName || "",
      status: "Not Started",
      eta: "",
      projectPrice: "",
      escrowAmount: "",
      releasedPayment: "",
      paymentStatus: "Pending",
      upworkAccount:"",
      source: "Upwork",
      upfrontPayment: "",
      paymentMethod: "",
      paymentMethodNote: "",
      sourceBid: "",
      milestones: [] as Milestone[],
      resourceCosts: [] as ResourceCost[],
      billingType: "Hourly",
      hourlyRate: "",
      estimatedHours: "",
      portalAccess: true,
      sendWelcomeEmail: true,
      grantWalletCredits: false,
      welcomeBonusAmount: "",
    });
    setUseMilestones(false);
    setMilestoneCount(2);
    setOriginMode("");
    setBidSearch("");
    setAiCatReason("");
    setStep(1);
    setDraftId(null);
    setExistingAttachments([]);

    // Keep the form mounted while the credentials modal is up; closing it runs
    // onSuccess (which typically closes the drawer).
    if (!showedCreds) onSuccess?.();
  };

  // Category options: DB-configured project types unioned with the canonical
  // default categories (Graphic Design, Website, Amazon & Ecommerce, …).
  const categoryOptions = [
    { value: "", label: "Select Category" },
    ...mergeCategoryNames(types.map((t) => t.name)).map((n) => ({ value: n, label: n })),
  ];

  // Subcategory options for the selected Category: DB sub-types + code defaults
  // (e.g. Graphic Design → the 21 deliverables from the guide).
  const subcategoryOptions = subcategoriesFor(
    formData.projectType,
    activeSubTypes.map((s) => s.name)
  );

  const projectStatusOptions = [
    { value: "Not Started", label: "Not Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  const paymentStatusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Partially Released", label: "Partially Released" },
    { value: "Paid", label: "Paid" },
  ];

  const clientOptions = [
    { value: "", label: "Select a contact…" },
    ...clients.map((client: any) => ({
      value: client._id,
      // Show name + system username so duplicate names are distinguishable.
      label: client.username ? `${client.name} · @${client.username}` : client.name,
    })),
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* 4-STEP WIZARD PROGRESS BAR — fixed band under the modal header;
          completed steps are clickable to go back. */}
      <div className="px-6 sm:px-8 py-4 bg-slate-50/80 border-b border-slate-200 shrink-0">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const state = n === step ? "current" : n < step ? "done" : "todo";
            return (
              <Fragment key={label}>
                <button
                  type="button"
                  onClick={() => { if (n < step) setStep(n); }}
                  className={`flex items-center gap-2.5 min-w-0 ${n < step ? "cursor-pointer" : "cursor-default"} ${state === "todo" ? "opacity-70" : ""}`}
                  title={label}
                >
                  <span
                    className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      state === "current"
                        ? "bg-primary text-white ring-4 ring-blue-100 shadow-sm"
                        : state === "done"
                        ? "bg-primary text-white"
                        : "bg-white border border-slate-300 text-slate-500 font-semibold"
                    }`}
                  >
                    {state === "done" ? "✓" : n}
                  </span>
                  <span
                    className={`hidden sm:block truncate text-xs ${
                      state === "current" ? "font-bold text-primary" : state === "done" ? "font-semibold text-primary" : "font-medium text-slate-600"
                    }`}
                  >
                    {label}
                  </span>
                </button>
                {n < STEPS.length && <div className="flex-1 h-px bg-slate-200 mx-1 sm:mx-2" />}
              </Fragment>
            );
          })}
        </div>
        {draftId && (
          <div className="flex items-center gap-2 mt-2.5">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">DRAFT</span>
            <span className="text-[11px] text-slate-500">Resuming a saved draft — your previous entries are loaded below.</span>
          </div>
        )}
      </div>

      {/* MODAL BODY — the only scrolling region; steps hide, never unmount */}
      <div className="flex-1 overflow-y-auto cre-scroll px-6 sm:px-8 py-6 space-y-6 bg-white">

      {/* STEP 1 — Project Information (hidden, not unmounted — data is preserved).
          Two-column layout per the approved "Project Creation Modal" design. */}
      <div className={step === 1 ? "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-start" : "hidden"}>
      {/* How was this project won? Direct client, or a Won bid.
          (An Invitation bid automatically marks the project INVITED.) */}
      <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-2.5">
        <label className="block text-sm font-semibold text-gray-800">
          How was this project won? <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {([
            { key: "direct", title: "Direct Client", sub: "Off-platform / referral" },
            { key: "bid", title: "From a Won Bid", sub: "Upwork bid or invitation" },
          ] as const).map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => {
                setOriginMode(o.key);
                if (o.key === "direct") {
                  setFormData((p) => ({ ...p, source: "Direct", sourceBid: "" }));
                  setBidSearch("");
                } else {
                  setFormData((p) => ({ ...p, source: p.sourceBid ? p.source : "Upwork" }));
                }
              }}
              className={`text-left rounded-lg border px-3 py-2.5 transition ${
                originMode === o.key
                  ? "border-emerald-500 bg-white ring-2 ring-emerald-200"
                  : "border-emerald-200 bg-white/60 hover:bg-white"
              }`}
            >
              <span className="block text-[13px] font-bold text-gray-800">{o.title}</span>
              <span className="block text-[11px] text-gray-500">{o.sub}</span>
            </button>
          ))}
        </div>

        {originMode === "bid" && (
          <div className="space-y-2">
            <input
              value={bidSearch}
              onChange={(e) => setBidSearch(e.target.value)}
              placeholder="Filter won bids — job title, contact, account…"
              className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
            <SelectField
              label=""
              name="sourceBid"
              value={formData.sourceBid}
              onChange={(e) => applyBid(e.target.value)}
              options={[
                { value: "", label: filteredWonBids.length ? "Select the Won bid…" : "No Won bids match your filter" },
                ...filteredWonBids.map((b: any) => {
                  const cname = b.contactRef?.name || "";
                  const uname = b.contactRef?.username || "";
                  const who = [cname, uname && `@${uname}`].filter(Boolean).join(" · ");
                  const inv = b.entryType === "Invitation" ? " · INVITATION" : b.entryType === "Directly Awarded" ? " · DIRECT AWARD" : "";
                  return {
                    value: b._id,
                    label: `${b.jobTitle || "Untitled"} · $${Number(b.bidAmount || 0).toLocaleString()}${who ? ` — ${who}` : ""}${inv}`,
                  };
                }),
              ]}
            />
            {selectedBid && (
              <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 flex-wrap">
                Pre-filled from the selected bid.
                {selectedBid.entryType === "Invitation" && (
                  <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-violet-100 text-violet-700">
                    INVITED — this project will be marked as an Invitation win
                  </span>
                )}
              </p>
            )}
          </div>
        )}
        {originMode === "direct" && (
          <p className="text-[11px] text-amber-700">
            <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 mr-1">DIRECT</span>
            This project will be marked as a Direct client project.
          </p>
        )}
      </div>

      <div>
        <InputField
          label="Project Name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Enterprise SaaS Cloud Architecture & UI Refactor"
          name="name"
          required
        />
        <p className="text-[11px] text-slate-400 mt-1">Official client-facing title</p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <label className="block text-sm font-medium text-gray-700">
            Assigned Contact <span className="text-red-500">*</span>
          </label>
          <span className="flex items-center gap-2">
            {/* Brand/Tenant chip — inherited from the contact, never selected manually */}
            {selectedContact && (
              <span
                className="text-[10px] font-semibold text-primary bg-blue-50 px-1.5 py-0.5 rounded"
                title="Brand / Tenant — inherited from the contact, never selected manually"
              >
                {(typeof selectedContact.tenant === "object" && selectedContact.tenant?.name) ||
                  tenantOptions.find((t) => t._id === (typeof selectedContact.tenant === "object" ? selectedContact.tenant?._id : selectedContact.tenant))?.name ||
                  "No brand"}
              </span>
            )}
            <button
              type="button"
              onClick={() => setContactModalOpen(true)}
              className="text-primary text-xs font-semibold hover:underline flex items-center gap-1"
            >
              <Plus size={13} /> New contact
            </button>
          </span>
        </div>
        <select
          name="assignedContact"
          value={formData.assignedContact}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
        >
          {clientOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <p className="text-[11px] text-slate-400 mt-1">Primary contact &amp; billing recipient</p>
      </div>

      {/* Category — design row 2 (left) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Category <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={suggestCategory}
            disabled={aiCatLoading}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:bg-primary/5 px-2.5 py-1 rounded-md disabled:opacity-50"
            title="AI picks the closest category from the questionnaire answers / job description — review before saving"
          >
            <Sparkles size={13} className={aiCatLoading ? "animate-pulse" : ""} />
            {aiCatLoading ? "Suggesting…" : "Suggest with AI"}
          </button>
        </div>
        <select
          name="projectType"
          value={formData.projectType}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
        >
          {categoryOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {aiCatReason && (
          <p className="text-[11px] text-primary/80 mt-1">
            AI: {aiCatReason} — please review this against the questionnaire answers / job description.
          </p>
        )}
      </div>

      {/* Subcategory — dynamic based on the selected Category (design row 2, right) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Subcategory <span className="text-slate-400 font-normal text-xs">(Dynamic based on Category)</span>
        </label>
        <select
          name="projectSubType"
          value={formData.projectSubType}
          onChange={handleChange}
          disabled={subcategoryOptions.length === 0}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">{subcategoryOptions.length ? "Select Subcategory" : "No subcategories for this category"}</option>
          {subcategoryOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {contactMissingInfo && (
        <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-800">
            <strong>{selectedContact?.name}</strong> is missing{" "}
            {!selectedContact?.email && !selectedContact?.phone
              ? "an email and phone number"
              : !selectedContact?.email
              ? "an email address"
              : "a phone number"}
            . The client won't receive automated emails (escrow/first-milestone requests, welcome & onboarding, milestone completion, next-milestone) until this is added on the contact.
          </div>
        </div>
      )}

      {/* Client Portal Access — provisions a Client Workspace on project creation */}
      <div className="md:col-span-2 rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound size={15} className="text-[#0058c3]" />
          <h3 className="text-sm font-semibold text-gray-800">Client Portal Access</h3>
        </div>
        <p className="text-[11px] text-gray-500 -mt-1">
          A secure Client Workspace account is created automatically when this project is saved.
          If the contact already has an account, the project is linked to it (no duplicate).
        </p>

        <label className="flex items-center gap-2.5 text-[13px] text-gray-700 cursor-pointer">
          <input type="checkbox" name="portalAccess" checked={formData.portalAccess} onChange={handleChange} className="w-4 h-4 accent-[#0058c3]" />
          Enable portal access for this client
        </label>

        {formData.portalAccess && (
          <div className="pl-6 space-y-2.5 border-l-2 border-blue-100">
            <label className="flex items-center gap-2.5 text-[13px] text-gray-700 cursor-pointer">
              <input type="checkbox" name="sendWelcomeEmail" checked={formData.sendWelcomeEmail} onChange={handleChange} className="w-4 h-4 accent-[#0058c3]" />
              Send welcome email with login details (when an email is on file)
            </label>
            <label className="flex items-center gap-2.5 text-[13px] text-gray-700 cursor-pointer">
              <input type="checkbox" name="grantWalletCredits" checked={formData.grantWalletCredits} onChange={handleChange} className="w-4 h-4 accent-[#0058c3]" />
              Grant a welcome wallet credit
            </label>
            {formData.grantWalletCredits && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-gray-500">Amount (USD)</span>
                <input
                  type="number" name="welcomeBonusAmount" min="0"
                  value={formData.welcomeBonusAmount} onChange={handleChange}
                  placeholder="0"
                  className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        {/* No native `required` — the wizard's own validation handles it (a
            native attr on a hidden step would silently block final submit). */}
        <SelectField
          label="Originating Proposal *"
          name="proposal"
          value={formData.proposal}
          onChange={handleChange}
          options={[
            { value: "", label: availableProposals.length ? "Select the proposal this project was won with…" : "No unlinked proposals available" },
            ...availableProposals.map((p: any) => ({ value: p._id, label: p.title })),
          ]}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Required. A proposal already linked to another project stays there — it can't be reused.
        </p>
      </div>

      {/* Client questionnaire — required to CREATE; a Draft can wait for it.
          Auto-linked when the contact is chosen. */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Client Questionnaire <span className="text-red-500">*</span>
          </label>
          {formData.linkedQuestionnaire && (
            <a
              href={(questionnaires.find((q: any) => q._id === formData.linkedQuestionnaire) as any)?.shareUrl || "/questionnaires"}
              target="_blank"
              rel="noreferrer"
              className="text-primary text-xs font-semibold hover:underline"
            >
              View Questionnaire
            </a>
          )}
        </div>
        <select
          name="linkedQuestionnaire"
          value={formData.linkedQuestionnaire}
          onChange={(e) => setFormData((p) => ({ ...p, linkedQuestionnaire: e.target.value }))}
          disabled={!formData.assignedContact || eligibleQuestionnaires.length === 0}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {!formData.assignedContact
              ? "Select a contact first…"
              : eligibleQuestionnaires.length
              ? "Select the submitted questionnaire…"
              : "No submitted questionnaire available for this contact"}
          </option>
          {eligibleQuestionnaires.map((q: any) => (
            <option key={q._id} value={q._id}>
              {q.name} — submitted
            </option>
          ))}
        </select>
        {formData.assignedContact && eligibleQuestionnaires.length === 0 ? (
          <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
            <div className="text-[12px] text-red-800">
              <strong>You cannot create this project until a questionnaire is linked.</strong>{" "}
              The client hasn't submitted one yet
              {contactQuestionnaires.some((q: any) => q.projectId?._id || q.projectId)
                ? " (their existing questionnaire is already linked to another project)"
                : ""}
              . Share the questionnaire with the client — or{" "}
              <a href="/questionnaires" target="_blank" rel="noreferrer" className="font-semibold underline">
                submit one yourself
              </a>{" "}
              based on your discussion with the client.
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-gray-400 mt-1">
            Auto-linked from the contact's submitted questionnaire. Its answers also feed the AI description &amp; category below.
          </p>
        )}
      </div>


      <InputField
        label="Supervisor"
        value={formData.supervisor}
        name="supervisor"
        disabled
      />

      {/* Status — design row 4 (right). Default: Not Started */}
      <SelectField
        label="Status"
        name="status"
        value={formData.status}
        onChange={handleChange}
        options={projectStatusOptions}
      />

      <InputField
        label="ETA / Expected Delivery"
        value={formData.eta}
        onChange={handleChange}
        type="date"
        name="eta"
      />

      {/* Upwork Account — shown for Upwork/Invitation sources; N/A for Direct */}
      {!isDirect ? (
        <SelectField
          label={`Upwork Account (Source: ${formData.source})`}
          name="upworkAccount"
          value={formData.upworkAccount}
          onChange={handleChange}
          options={[
            { value: "", label: "Not Assigned" },
            ...accounts.map((a: { accountId: string; accountName: string }) => ({
              value: a.accountId,
              label: a.accountName,
            })),
          ]}
        />
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Upwork Account <span className="text-slate-400 font-normal text-xs">(Source: Direct)</span>
          </label>
          <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400">
            Not Applicable — Direct project
          </div>
        </div>
      )}

      {/* Project Description — full width (design row 6) */}
      <div className="md:col-span-2">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Project Description <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={generateDescription}
            disabled={aiGenLoading}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:bg-primary/5 px-2.5 py-1 rounded-md disabled:opacity-50"
            title="AI drafts a description from the name, category & questionnaire — you can edit it after"
          >
            <Sparkles size={13} className={aiGenLoading ? "animate-pulse" : ""} />
            {aiGenLoading ? "Generating…" : "Generate with AI"}
          </button>
        </div>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          placeholder="Describe the scope, objectives, and deliverables… — or paste the job brief and click Generate with AI."
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white resize-none"
        />
        <p className="text-[11px] text-slate-400 mt-1">Summarize high-level objectives, scope, and delivery criteria. AI-generated text is fully editable — the onboarding requirements below are generated from it.</p>
      </div>

      {/* S3 — Requirement checklist + AI onboarding analysis. Always includes the
          baseline items (credentials, scope, winning context) so blockers surface
          BEFORE the project is created. */}
      {formData.projectType && (checklist.length > 0 || aiChecklist.length > 0) && (
        <div className="md:col-span-2 rounded-xl border border-outline-variant bg-surface-container-low p-3.5 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-semibold text-on-surface">Onboarding Requirements</h4>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => generateChecklist()}
                disabled={aiChecklistLoading}
                title="Regenerate the requirements from the current description"
                className="text-primary text-xs font-semibold hover:underline flex items-center gap-1 disabled:opacity-50"
              >
                <Sparkles size={13} /> {aiChecklistLoading ? "Generating…" : aiChecklist.length ? "Regenerate" : "Generate"}
              </button>
              <button
                type="button"
                onClick={runAiCheck}
                disabled={aiLoading}
                className="text-primary text-xs font-semibold hover:underline flex items-center gap-1 disabled:opacity-50"
              >
                <Sparkles size={13} /> {aiLoading ? "Analyzing…" : "AI check"}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-on-surface-variant -mt-1">
            {aiChecklistLoading
              ? "Reading the description and working out what this project actually needs…"
              : "Generated from the project description — only the things that would block the work."}{" "}
            Tick an item once you have actually <strong>received and verified</strong> it; your name and the time are
            recorded and sent to ERP with the project.
          </p>
          <div className="flex flex-col gap-1.5">
            {effectiveChecklist.map((c) => {
              const isMissing = aiResult?.missing.includes(c.label);
              return (
                <label key={c.label} className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={Boolean(checkMet[c.label])}
                    onChange={(e) => setCheckMet((m) => ({ ...m, [c.label]: e.target.checked }))}
                  />
                  <span className={isMissing ? "text-amber-700 font-medium" : "text-on-surface"}>
                    {c.label}
                    {c.required !== false && <span className="text-error"> *</span>}
                    {(c as any).category && (
                      <span className="ml-1.5 px-1.5 py-px rounded-full text-[10px] font-bold bg-slate-100 text-slate-500">
                        {(c as any).category}
                      </span>
                    )}
                    {isMissing && <span className="ml-1 text-[11px] text-amber-600">(missing)</span>}
                    {(c as any).detail && (
                      <span className="block text-[11px] text-outline font-normal mt-0.5">{(c as any).detail}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {aiResult && (
            <div className={`text-[12px] rounded-lg p-2 ${aiResult.missing.length ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
              {aiResult.missing.length ? (
                <>Missing: <strong>{aiResult.missing.join(", ")}</strong>. {aiResult.recommendation}</>
              ) : (
                <span className="flex items-center gap-1"><CheckCircle2 size={13} /> All required requirements covered.</span>
              )}
              {!aiResult.aiUsed && <div className="text-[11px] opacity-70 mt-0.5">AI unavailable — add a description and retry, or tick items manually.</div>}
            </div>
          )}
        </div>
      )}
      </div>

      {/* STEP 2 — Milestones & Payments */}
      <div className={step === 2 ? "space-y-5" : "hidden"}>
      <InputField
        label="Total Project Value ($)"
        value={formData.projectPrice}
        onChange={handleChange}
        placeholder="0.00"
        type="number"
        name="projectPrice"
      />

      {/* Billing Type — effort estimation (Hourly auto-calculates the value) */}
      <div className="rounded-xl border border-outline-variant p-3.5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-[13px] font-semibold text-on-surface">Billing Type</label>
          <div className="flex items-center gap-3">
            {/* Total Project Value shown inline with the billing type */}
            <div className="text-right leading-tight">
              <div className="text-[10px] font-semibold text-on-surface-variant uppercase tracking-wide">Total Value</div>
              <div className="text-[15px] font-bold text-primary">
                ${(
                  formData.billingType === "Hourly"
                    ? (Number(formData.projectPrice) || (Number(formData.hourlyRate) || 0) * (Number(formData.estimatedHours) || 0))
                    : (Number(formData.projectPrice) || 0)
                ).toLocaleString()}
              </div>
            </div>
            <div className="flex rounded-lg border border-outline-variant overflow-hidden text-xs font-semibold">
              {(["Hourly", "Fixed"] as const).map((bt) => (
                <button
                  key={bt}
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, billingType: bt }))}
                  className={`px-3 py-1.5 ${formData.billingType === bt ? "bg-primary text-white" : "bg-white text-on-surface-variant"}`}
                >
                  {bt === "Fixed" ? "Fixed Price" : "Hourly"}
                </button>
              ))}
            </div>
          </div>
        </div>
        {formData.billingType === "Hourly" ? (
          <div className="grid grid-cols-3 gap-3">
            <InputField label="Hourly Rate ($/Hr)" type="number" name="hourlyRate" value={formData.hourlyRate}
              onChange={(e) => {
                const rate = e.target.value;
                const val = (Number(rate) || 0) * (Number(formData.estimatedHours) || 0);
                setFormData((p) => ({ ...p, hourlyRate: rate, projectPrice: val ? String(val) : p.projectPrice }));
              }} placeholder="0" />
            <InputField label="Estimated Hours" type="number" name="estimatedHours" value={formData.estimatedHours}
              onChange={(e) => {
                const hrs = e.target.value;
                const val = (Number(formData.hourlyRate) || 0) * (Number(hrs) || 0);
                setFormData((p) => ({ ...p, estimatedHours: hrs, projectPrice: val ? String(val) : p.projectPrice }));
              }} placeholder="0" />
            <InputField label="Project Value (auto, editable)" type="number" name="projectPrice" value={formData.projectPrice} onChange={handleChange} placeholder="0.00" />
          </div>
        ) : (
          <InputField label="Estimated Hours (for cost & profitability)" type="number" name="estimatedHours" value={formData.estimatedHours} onChange={handleChange} placeholder="0" />
        )}
        <p className="text-[11px] text-on-surface-variant">Estimated hours are captured for resource planning, cost analysis, profitability &amp; actual-vs-estimated tracking.</p>
      </div>

      {/* Recommend milestone-based collection for higher-value projects */}
      {showMilestonePrompt && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 space-y-3">
          <p className="text-[13px] text-amber-800">
            <strong>Heads up:</strong> this project is over $100. We recommend collecting
            payment in <strong>milestones</strong> for safer delivery.
          </p>
          <label className="flex items-center gap-2 text-[13px] font-semibold text-amber-900">
            <input
              type="checkbox"
              checked={useMilestones}
              onChange={(e) => setUseMilestones(e.target.checked)}
              className="rounded border-amber-300"
            />
            Yes, deliver this project using milestones
          </label>
          {useMilestones && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[11px] font-bold text-amber-700 mb-1">
                  Number of milestones
                </label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={milestoneCount}
                  onChange={(e) => setMilestoneCount(Number(e.target.value))}
                  className="w-24 px-2 py-1.5 border border-amber-300 rounded-md text-sm bg-white"
                />
              </div>
              <button
                type="button"
                onClick={autoSplitMilestones}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition"
              >
                Auto-split ${projectValue.toLocaleString()} equally
              </button>
            </div>
          )}
        </div>
      )}

      {isDirect ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="Upfront Payment ($)"
              value={formData.upfrontPayment}
              onChange={handleChange}
              placeholder="0.00"
              type="number"
              name="upfrontPayment"
            />
            <InputField
              label="Amount Received ($)"
              value={formData.releasedPayment}
              onChange={handleChange}
              placeholder="0.00"
              type="number"
              name="releasedPayment"
            />
          </div>
          <SelectField
            label="Payment Method"
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleChange}
            options={[
              { value: "", label: "Select Method" },
              { value: "PayPal", label: "PayPal" },
              { value: "Payoneer", label: "Payoneer" },
              { value: "Bank Transfer", label: "Bank Transfer" },
              { value: "Stripe", label: "Stripe" },
              { value: "Wise", label: "Wise" },
              { value: "Cash", label: "Cash" },
              { value: "Other", label: "Other" },
            ]}
          />
          {formData.paymentMethod === "Other" && (
            <InputField
              label="Payment Method (specify)"
              value={formData.paymentMethodNote}
              onChange={handleChange}
              placeholder="e.g. Crypto, Cheque…"
              name="paymentMethodNote"
            />
          )}
        </>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Escrow Amount"
            value={formData.escrowAmount}
            onChange={handleChange}
            placeholder="0.00"
            type="number"
            name="escrowAmount"
          />
          <InputField
            label="Released Payment"
            value={formData.releasedPayment}
            onChange={handleChange}
            placeholder="0.00"
            type="number"
            name="releasedPayment"
          />
        </div>
      )}

      <SelectField
        label="Project Status"
        name="status"
        value={formData.status}
        onChange={handleChange}
        options={projectStatusOptions}
      />

      <SelectField
        label="Payment Status"
        name="paymentStatus"
        value={formData.paymentStatus}
        onChange={handleChange}
        options={paymentStatusOptions}
      />

      <MilestonesEditor
  milestones={formData.milestones}
  onChange={(milestones) => setFormData((prev) => ({ ...prev, milestones }))}
  projectContext={{ name: formData.name, description: formData.description, category: formData.projectType }}
/>

      {/* Payment Summary — auto-calculated; milestone total must match the value */}
      {formData.milestones.length > 0 && (() => {
        const value = effectiveValue();
        const sumBy = (pred: (s: string) => boolean) =>
          formData.milestones.reduce((s: number, m: any) => s + (pred(m.status) ? Number(m.amount) || 0 : 0), 0);
        const msTotal = formData.milestones.reduce((s: number, m: any) => s + (Number(m.amount) || 0), 0);
        const released = sumBy((s) => s === "Released" || s === "Completed");
        const escrow = sumBy((s) => s === "In Escrow");
        const pending = msTotal - released - escrow;
        const mismatch = value > 0 && Math.abs(msTotal - value) > 0.01;
        const money = (n: number) => `$${(Math.round(n * 100) / 100).toLocaleString()}`;
        return (
          <div className={`rounded-xl border p-3.5 space-y-2 ${mismatch ? "border-amber-300 bg-amber-50/50" : "border-outline-variant"}`}>
            <h4 className="text-[13px] font-semibold text-on-surface">Payment Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
              {[
                ["Project Value", money(value)],
                ["Milestone Total", money(msTotal)],
                ["Released", money(released)],
                ["In Escrow", money(escrow)],
                ["Pending", money(pending)],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-gray-50 border border-gray-100 p-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{l}</p>
                  <p className="text-[13px] font-extrabold text-gray-800">{v}</p>
                </div>
              ))}
            </div>
            {mismatch && (
              <p className="text-[12px] text-amber-800 flex items-center gap-1.5">
                <AlertTriangle size={13} className="shrink-0" />
                Payment milestone total does not match the project value. Please review the payment schedule.
              </p>
            )}
          </div>
        );
      })()}

      {/* Resource costs — delivery cost & estimated net value */}
      <ResourceCostsEditor
        costs={formData.resourceCosts}
        onChange={(rc) => setFormData((prev) => ({ ...prev, resourceCosts: rc }))}
        projectValue={effectiveValue()}
      />
      </div>

      {/* STEP 3 — Documents & Assets */}
      <div className={step === 3 ? "space-y-5" : "hidden"}>
      {/* Files already saved on the draft/project (uploaded on a previous save) */}
      {existingAttachments.length > 0 && (
        <div className="rounded-xl border border-outline-variant p-3.5 space-y-1.5">
          <h4 className="text-[13px] font-semibold text-on-surface">
            Already attached ({existingAttachments.length})
          </h4>
          {existingAttachments.map((a: any, i: number) => (
            <div key={a._id || i} className="flex items-center justify-between bg-surface-container-low rounded-lg px-3 py-1.5">
              <span className="flex items-center gap-2 text-[12px] text-on-surface truncate">
                <FileText size={14} className="text-primary shrink-0" /> {a.name}
              </span>
              <span className="text-[10px] font-bold text-outline uppercase tracking-wide shrink-0 ml-2">{a.category || "Other"}</span>
            </div>
          ))}
        </div>
      )}
      {/* S1 — Project documentation (proposal docs, briefs/specs, client assets) */}
      <div className="rounded-xl border border-outline-variant p-3.5 space-y-3">
        <h4 className="text-[13px] font-semibold text-on-surface">Project Documents</h4>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={fileCategory}
            onChange={(e) => setFileCategory(e.target.value)}
            className="px-2 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="Document">Document</option>
            <option value="Asset">Asset</option>
            <option value="Proposal">Proposal</option>
            <option value="Brief/Spec">Brief / Spec</option>
            <option value="Client Asset">Client Asset</option>
            <option value="Other">Other</option>
          </select>
          <label className="px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs font-semibold text-on-surface cursor-pointer hover:bg-surface-container flex items-center gap-1.5">
            <Upload size={13} /> Choose files
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
            />
          </label>
          <span className="text-[11px] text-on-surface-variant">JPG, PNG, PDF · multiple allowed</span>
        </div>
        {files.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-surface-container-low rounded-lg px-3 py-1.5">
                <span className="flex items-center gap-2 text-[12px] text-on-surface truncate">
                  <FileText size={14} className="text-primary shrink-0" /> {f.name}
                  <span className="text-outline">({Math.round(f.size / 1024)} KB)</span>
                </span>
                <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="p-0.5 text-outline hover:text-error">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job Description upload (PDF / DOCX) */}
      <div className="border border-outline-variant rounded-xl p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-on-surface">Job Description (JD)</p>
            <span className="text-[11px] text-on-surface-variant">PDF or DOCX · attached to the project</span>
          </div>
          <label className="px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-xs font-semibold text-on-surface cursor-pointer hover:bg-surface-container flex items-center gap-1.5">
            <Upload size={13} /> Upload JD
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (!/\.(pdf|docx?)$/i.test(f.name)) { toast.error("Only PDF or DOCX files are allowed for the JD."); return; }
                setJdFile(f);
              }}
            />
          </label>
        </div>
        {jdFile && (
          <div className="mt-2 flex items-center justify-between bg-surface-container-low rounded-lg px-3 py-1.5">
            <span className="flex items-center gap-2 text-[12px] text-on-surface truncate">
              <FileText size={14} className="text-primary shrink-0" /> {jdFile.name}
              <span className="text-outline">({Math.round(jdFile.size / 1024)} KB)</span>
            </span>
            <button type="button" onClick={() => setJdFile(null)} className="p-0.5 text-outline hover:text-error"><X size={14} /></button>
          </div>
        )}
      </div>
      </div>

      {/* STEP 4 — Review & Submit: everything at a glance before creating */}
      <div className={step === 4 ? "space-y-4" : "hidden"}>
        <div className="rounded-xl border border-outline-variant p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="text-sm font-bold text-on-surface">Review before creating</h4>
            <div className="flex items-center gap-3 text-[11px] font-semibold">
              {STEPS.slice(0, 3).map((s, i) => (
                <button key={s} type="button" onClick={() => setStep(i + 1)} className="text-primary hover:underline">
                  Edit {s}
                </button>
              ))}
            </div>
          </div>
          {draftId && (
            <p className="text-[11px] rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5">
              This is a saved <strong>Draft</strong> — Create Project converts it into an active project (invoices, portal access and ERP eligibility start from that moment).
            </p>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[13px] text-gray-800">
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Project Name</span>{formData.name || "—"}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Won Via</span>{formData.source}{selectedBid ? ` — ${selectedBid.jobTitle || "bid"}` : ""}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Contact</span>{selectedContact?.name || "—"}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Category</span>{formData.projectType || "—"}{formData.projectSubType ? ` · ${formData.projectSubType}` : ""}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Proposal</span>{(proposals.find((p: any) => p._id === formData.proposal) as any)?.title || "—"}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Questionnaire</span>{(questionnaires.find((q: any) => q._id === formData.linkedQuestionnaire) as any)?.name || "—"}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Billing</span>{formData.billingType}{formData.billingType === "Hourly" && formData.hourlyRate ? ` · $${formData.hourlyRate}/hr × ${formData.estimatedHours || 0}h` : ""}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Total Value</span>${(Number(formData.projectPrice) || 0).toLocaleString()}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">ETA</span>{formData.eta || "—"}</div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Status</span>{formData.status}</div>
            <div className="sm:col-span-2">
              <span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Milestones</span>
              {formData.milestones.length ? (
                <>
                  {formData.milestones.map((m: any, i: number) => {
                    const dels = (m.deliverables || []).filter((d: any) => (d.title || "").trim());
                    return (
                      <span key={i} className="block text-[12px]">
                        {i + 1}. {m.title || `Milestone ${i + 1}`} · ${(Number(m.amount) || 0).toLocaleString()}
                        {m.duration ? ` · ${m.duration}` : ""}
                        {dels.length ? ` · ${dels.length} deliverable${dels.length === 1 ? "" : "s"}` : ""}
                      </span>
                    );
                  })}
                  <span className="block text-[11px] text-gray-400 mt-0.5">
                    Total $
                    {formData.milestones.reduce((s, m) => s + (Number(m.amount) || 0), 0).toLocaleString()} — each
                    milestone gets its invoice automatically
                  </span>
                </>
              ) : (
                "None"
              )}
            </div>
            <div>
              <span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Resource Costs</span>
              {formData.resourceCosts.length
                ? `$${(Math.round(resourceCostTotal(formData.resourceCosts, effectiveValue()) * 100) / 100).toLocaleString()} — est. net $${(Math.round((effectiveValue() - resourceCostTotal(formData.resourceCosts, effectiveValue())) * 100) / 100).toLocaleString()}`
                : "Defaults from cost configuration"}
            </div>
            <div>
              <span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Documents & Assets</span>
              {existingAttachments.length ? `${existingAttachments.length} saved` : ""}
              {existingAttachments.length && (files.length || jdFile) ? " + " : ""}
              {files.length ? `${files.length} new file(s)` : ""}
              {jdFile ? (files.length ? " + JD" : "JD") : ""}
              {!existingAttachments.length && !files.length && !jdFile ? "None" : ""}
            </div>
            <div><span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400">Client Portal</span>{formData.portalAccess ? "Enabled" : "Disabled"}</div>
          </div>
          {formData.description && (
            <div>
              <span className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">Description</span>
              <p className="text-[12px] text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{formData.description}</p>
            </div>
          )}
          <p className="text-[11px] text-gray-400">Need to change something? Use Back or click a completed step above — your entries are preserved.</p>
        </div>
      </div>

      </div>

      {/* MODAL FOOTER — workflow navigation & actions (fixed band).
          Save as Draft works from ANY step; the project is only created from
          the Review step. */}
      <div className="px-6 sm:px-8 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 shrink-0 flex-wrap">
        <button
          type="button"
          onClick={saveDraft}
          disabled={savingDraft}
          title="Save everything entered so far as a Draft — reopen and complete it later (e.g. once the client submits the questionnaire)"
          className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          {savingDraft ? "Saving…" : draftId ? "Save Draft" : "Save as Draft"}
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 1}
            className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors ${
              step === 1
                ? "text-slate-400 bg-slate-100 border-slate-200 cursor-not-allowed"
                : "text-slate-600 hover:text-slate-900 bg-white border-slate-300 hover:bg-slate-50"
            }`}
          >
            Back
          </button>
          {step < 4 ? (
            <button
              type="button"
              onClick={goNext}
              className="px-5 py-2 text-xs font-bold text-white bg-primary hover:brightness-110 rounded-lg flex items-center gap-1.5 shadow-sm transition-all"
            >
              Continue to {STEPS[step]} <span aria-hidden>&#8594;</span>
            </button>
          ) : (
            <Button type="submit" isLoading={isCreatingProject || finalizing} className="!px-6 !py-2 text-xs">
              {isCreatingProject || finalizing ? "Creating..." : "Create Project"}
            </Button>
          )}
        </div>
      </div>

      {/* Inline create-contact modal */}
      {contactModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setContactModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">New Contact</h3>
              <button type="button" onClick={() => setContactModalOpen(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded-full">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  autoFocus
                  value={newContact.name}
                  onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))}
                  placeholder="Client / contact name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                  <input
                    value={newContact.email}
                    onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))}
                    placeholder="optional"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
                  <input
                    value={newContact.phone}
                    onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))}
                    placeholder="optional"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Brand / Company <span className="text-red-500">*</span></label>
                <select
                  value={newContact.tenant}
                  onChange={(e) => setNewContact((c) => ({ ...c, tenant: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                >
                  <option value="">Select the brand…</option>
                  {tenantOptions.map((t) => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-gray-400">Email &amp; phone let the client receive automated updates — add them if you can.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setContactModalOpen(false)} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNewContact}
                disabled={creatingContact || !newContact.name.trim()}
                className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-[#004b9e] disabled:opacity-50"
              >
                {creatingContact ? "Creating…" : "Create & Select"}
              </button>
            </div>
          </div>
        </div>
      )}

      {portalCreds && (
        <CredentialsModal
          credentials={portalCreds.credentials}
          clientName={portalCreds.clientName}
          emailSent={portalCreds.emailSent}
          onClose={() => {
            setPortalCreds(null);
            onSuccess?.();
          }}
        />
      )}

    </form>
  );
};

export default ProjectForm;
